import React, { useEffect, useMemo, useState } from 'react';
import { Modal, List, Button, Input, Tag, message, Space, Divider, Switch } from 'antd';
import { SearchOutlined, LockOutlined, UnlockOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { UpdatePracticaProblemaDepende } from "../../../../dao/UpdatePracticaProblemaDepende";
import JutgeColors from "../../../../styles/JutgeColors";

interface DependenciaModalProps {
    visible: boolean;
    onClose: (refresh: boolean) => void;
    id_activitat: string;
    targetProblem: any; // The problem we are editing
    allProblems: any[]; // List of all problems to choose from
}

// Extrae los ids de ejercicios prerequisito del valor crudo de 'depende'.
// 'depende' es un CSV que puede incluir "-1" (dependencia de juego) junto a ids reales.
// Ej: null -> []; "-1" -> []; "5,7" -> ["5","7"]; "-1,5,7" -> ["5","7"].
const parseIds = (raw: any): string[] => {
    if (raw === null || raw === undefined || raw === "" || raw === 0 || raw === "0") return [];
    return String(raw)
        .split(",")
        .map(s => s.trim())
        .filter(s => /^\d+$/.test(s) && s !== "0"); // descarta "-1" y vacios
};

// ¿El valor incluye la marca de dependencia de juego (-1)?
const hasGameFlag = (raw: any): boolean => {
    if (raw === null || raw === undefined) return false;
    return String(raw).split(",").map(s => s.trim()).includes("-1");
};

const DependenciaModal: React.FC<DependenciaModalProps> = ({ visible, onClose, id_activitat, targetProblem, allProblems }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [gameEnabled, setGameEnabled] = useState(false);

    // Inicializa el estado con la dependencia actual del ejercicio.
    useEffect(() => {
        if (visible && targetProblem) {
            setSelectedIds(parseIds(targetProblem.depende));
            setGameEnabled(hasGameFlag(targetProblem.depende));
            setSearchTerm('');
        }
    }, [visible, targetProblem]);

    const filteredProblems = useMemo(() => {
        if (!allProblems) return [];
        return allProblems.filter(p =>
            p.id !== targetProblem?.id && // evita auto-dependencia
            (
                (p.prac || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (p.id + "").includes(searchTerm)
            )
        );
    }, [allProblems, searchTerm, targetProblem]);

    const toggleId = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const nombreDe = (id: string) => {
        const p = allProblems?.find(x => (x.id + "") === id);
        return p ? p.prac : `#${id}`;
    };

    const persist = async (value: string | null) => {
        setLoading(true);
        try {
            const resp = await UpdatePracticaProblemaDepende(id_activitat, targetProblem.id, value);
            if (resp.ok) {
                message.success("Dependencia actualizada correctamente");
                onClose(true);
            } else {
                message.error("Error al actualizar dependencia");
            }
        } catch (e) {
            console.error(e);
            message.error("Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    // Construye el CSV combinando la marca de juego (-1) + los ids prerequisito.
    // Si no hay nada, guarda null (sin dependencia).
    const handleSave = () => {
        const parts: string[] = [];
        if (gameEnabled) parts.push("-1");
        parts.push(...selectedIds);
        persist(parts.length > 0 ? parts.join(",") : null);
    };

    const handleClearAll = () => {
        setGameEnabled(false);
        setSelectedIds([]);
        persist(null);
    };

    const currentIds = parseIds(targetProblem?.depende);
    const currentGame = hasGameFlag(targetProblem?.depende);

    return (
        <Modal
            title={`Gestión de Dependencias: Ejercicio ${targetProblem?.id}`}
            open={visible}
            onCancel={() => onClose(false)}
            footer={null}
            destroyOnClose
            width={620}
        >
            {/* Resumen de la dependencia actual */}
            <div style={{ marginBottom: 12 }}>
                <strong>Dependencia actual: </strong>
                {!currentGame && currentIds.length === 0 ? (
                    <Tag color="green" style={{ fontSize: 14, padding: 5 }}>
                        <UnlockOutlined /> Ninguna (Libre acceso)
                    </Tag>
                ) : (
                    <Space size={[4, 4]} wrap>
                        {currentGame && (
                            <Tag color="purple" style={{ fontSize: 13, padding: '2px 6px' }}>🎮 Solo Juego</Tag>
                        )}
                        {currentIds.map(id => (
                            <Tag key={id} color="red" style={{ fontSize: 13, padding: '2px 6px' }}>
                                <LockOutlined /> {id} · {nombreDe(id)}
                            </Tag>
                        ))}
                    </Space>
                )}
            </div>

            {/* Toggle de dependencia de juego (combinable con prerequisitos) */}
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ padding: '10px 14px', background: '#faf5ff', borderRadius: 8, border: '1px solid #efdbff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ marginRight: 12 }}>
                    <div style={{ fontWeight: 600, color: '#722ed1', display: 'flex', alignItems: 'center', gap: 6 }}>
                        🎮 Dependencia de Juego
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        Visible exclusivamente dentro del juego (no aparece en la vista web del alumno).
                        Se puede combinar con prerequisitos: el ejercicio aparecerá en el juego y, además,
                        exigirá completar los ejercicios de la lista.
                    </div>
                </div>
                <Switch
                    checked={gameEnabled}
                    onChange={setGameEnabled}
                    checkedChildren="Sí"
                    unCheckedChildren="No"
                />
            </div>

            {/* Prerequisitos (siempre disponibles, también junto a juego) */}
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Prerequisitos seleccionados ({selectedIds.length})
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                    El ejercicio quedará bloqueado hasta que el alumno complete <b>TODOS</b> los
                    ejercicios de esta lista.
                </div>
                {selectedIds.length > 0 ? (
                    <Space size={[4, 4]} wrap style={{ marginBottom: 8 }}>
                        {selectedIds.map(id => (
                            <Tag
                                key={id}
                                color="red"
                                closable
                                onClose={(e) => { e.preventDefault(); toggleId(id); }}
                                style={{ fontSize: 13, padding: '2px 6px' }}
                            >
                                <LockOutlined /> {id} · {nombreDe(id)}
                            </Tag>
                        ))}
                    </Space>
                ) : (
                    <Tag color="default" style={{ marginBottom: 8 }}>Ninguno seleccionado</Tag>
                )}
            </div>

            <div style={{ marginBottom: 12 }}>
                <Input
                    prefix={<SearchOutlined />}
                    placeholder="Buscar por ID o nombre..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            <List
                pagination={{ pageSize: 5 }}
                dataSource={filteredProblems}
                renderItem={(item) => {
                    const idStr = item.id + "";
                    const checked = selectedIds.includes(idStr);
                    return (
                        <List.Item
                            actions={[
                                <Button
                                    type={checked ? "default" : "primary"}
                                    size="small"
                                    icon={checked ? <DeleteOutlined /> : <PlusOutlined />}
                                    danger={checked}
                                    onClick={() => toggleId(idStr)}
                                >
                                    {checked ? "Quitar" : "Añadir"}
                                </Button>
                            ]}
                        >
                            <List.Item.Meta
                                title={<span><Tag>ID: {item.id}</Tag> {item.prac}</span>}
                            />
                        </List.Item>
                    );
                }}
            />

            <Divider style={{ margin: '12px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Button danger icon={<DeleteOutlined />} loading={loading} onClick={handleClearAll}>
                        Quitar todas
                    </Button>
                    <Button
                        type="primary" icon={<SaveOutlined />} loading={loading}
                        onClick={handleSave}
                        style={{ backgroundColor: JutgeColors.colors.color4, borderColor: JutgeColors.colors.color4 }}
                    >
                        Guardar dependencias
                    </Button>
                </Space>
            </div>
        </Modal>
    );
};

export default DependenciaModal;
